import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

/**
 * Which design a visitor sees. Everyone sees the redesign (owner, 2026-10-27: "no preview prepare
 * for full release"). `?ui=classic` shows the earlier page at that address only: the choice isn't
 * remembered, so no browser is left on the earlier screens with no way back.
 */
export type UiMode = "next" | "classic";

export const DEFAULT_UI_MODE: UiMode = "next";
/** Where the preview remembered a choice. It's cleared: a choice no longer carries over. */
export const UI_MODE_STORAGE_KEY = "lc-ui-mode";

/** The earlier page when the address asks for it; otherwise the redesign. */
export function resolveUiMode(search: string): UiMode {
  return new URLSearchParams(search).get("ui") === "classic" ? "classic" : DEFAULT_UI_MODE;
}

/** Clears a choice the preview remembered on this device. */
export function forgetRememberedUiMode(storage: Pick<Storage, "removeItem"> | null) {
  try {
    storage?.removeItem(UI_MODE_STORAGE_KEY);
  } catch {
    // Blocked storage holds nothing to clear.
  }
}

function browserStorage(): Pick<Storage, "removeItem"> | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function useUiMode(): UiMode {
  const { search } = useLocation();
  useEffect(() => forgetRememberedUiMode(browserStorage()), []);
  return useMemo(() => resolveUiMode(search), [search]);
}
